import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Alert } from 'antd';
import cn from 'classnames';
import Container from 'components/common/Container';
import Button from 'components/antd/Button';
import MotionDetectionPremiumModal, { motionAlertsInvoiceRequestQueryKey } from 'components/MotionDetectionPremiumModal';
import { requestGetMotionAlertsInvoiceRequest } from 'pages/ActivityFeed/services';
import { ICustomMap } from 'types/ICustomMap';
import { textsActivityFeed, textsCommon } from 'texts/index';
import routes from 'config/routes';
import styles from './styles.module.scss';

const storageKeyFor = (workspaceId: string) => `motionDetectionLiveBannerDismissed:${workspaceId}`;

const MotionDetectionLiveBanner = () => {
    const { id_str: workspaceId, cap_settings } = useSelector(({ workspace }: ICustomMap) => workspace);
    const location = useLocation();

    const isMotionAlertsPaid = !!cap_settings?.is_motion_alerts_paid;

    const {
        data: invoiceRequestData,
        isLoading: isLoadingInvoiceRequest,
        isError: isInvoiceRequestError,
    } = useQuery({
        queryKey: [motionAlertsInvoiceRequestQueryKey, workspaceId],
        queryFn: () => requestGetMotionAlertsInvoiceRequest(),
        enabled: !isMotionAlertsPaid && !!workspaceId,
        retry: 1,
    });

    const invoiceRequestInProgress =
        !isInvoiceRequestError && !!invoiceRequestData?.respData?.invoice_request_in_progress;

    const shouldOfferBanner =
        !isMotionAlertsPaid && !isLoadingInvoiceRequest && !invoiceRequestInProgress;

    const isActivityFeed =
        location.pathname.includes(routes.activityFeed.root) || location.pathname === routes.home;

    const lsKey = workspaceId ? storageKeyFor(workspaceId) : null;
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (!lsKey || typeof window === 'undefined') {
            setDismissed(false);
            return;
        }
        setDismissed(localStorage.getItem(lsKey) === 'true');
    }, [lsKey]);

    const [premiumOpen, setPremiumOpen] = useState(false);

    const visible = shouldOfferBanner && !!workspaceId && !dismissed;

    const handleDismiss = useCallback(() => {
        if (!lsKey || typeof window === 'undefined') return;
        localStorage.setItem(lsKey, 'true');
        setDismissed(true);
    }, [lsKey]);

    const handleViewMore = useCallback(() => {
        setPremiumOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setPremiumOpen(false);
    }, []);

    const message = useMemo(
        () => (
            <div className={styles.message}>
                <div className={styles.messageText}>{textsActivityFeed.motionDetectionLiveBannerMessage}</div>
                <div className={styles.buttons}>
                    <Button type='primary' size='small' ghost onClick={handleViewMore}>
                        {textsActivityFeed.motionDetectionLiveBannerViewMore}
                    </Button>
                    <Button size='small' onClick={handleDismiss}>
                        {textsCommon.buttons.dismiss}
                    </Button>
                </div>
            </div>
        ),
        [handleDismiss, handleViewMore],
    );

    if (!visible) return null;

    return (
        <Container fullWidth>
            <Alert
                className={cn(styles.alert, { [styles.pushRight]: isActivityFeed })}
                type='success'
                showIcon
                message={message}
            />
            <MotionDetectionPremiumModal
                open={premiumOpen}
                onClose={handleCloseModal}
                isMotionAlertsPaid={isMotionAlertsPaid}
            />
        </Container>
    );
};

export default MotionDetectionLiveBanner;
